import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { WahooWorkoutType, WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { useSnackbarContext } from "@WahooFitness/wahoo-offline-mfe";
import FullScreenDialog from "@/components/FullScreenDialog";
import EditWorkoutProfile from "../EditWorkoutProfile/EditWorkoutProfile";
import useWorkoutProfiles from "../useWorkoutProfiles";
import NewOrExistingToggle from "./NewOrExistingToggle";
import TemplateChipList from "./TemplateChipList";
import useAddWorkoutProfile from "./useAddWorkoutProfile";
import { t } from "@lingui/macro";
import { MenuList } from "@WahooFitness/redesignr";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";

interface Props {
  closeParent: () => void;
  familyFilter?: WahooWorkoutTypeFamily;
}

const AddWorkoutProfile: React.FC<Props> = ({ closeParent, familyFilter }) => {
  const { palette } = useTheme();
  const { enqueueSnackbar } = useSnackbarContext();
  const [open, setOpen] = useState<boolean>(false);
  const [newProfile, setNewProfile] = useState<WSMWorkoutProfile>();
  const { createProfile, copyProfile, deleteProfile, getWorkoutProfile } =
    useWorkoutProfilesContext();

  const handleCreateProfile = useCallback(
    async (workoutProfileType: WahooWorkoutType, name: string) => {
      const result = await createProfile(workoutProfileType, name);
      if (result) {
        setNewProfile(result);
        setOpen(true);
      }
    },
    [createProfile]
  );

  const handleCopyProfile = useCallback(
    async (profileId: string) => {
      const result = await copyProfile(profileId);
      if (result) {
        setNewProfile(result);
        setOpen(true);
      }
    },
    [copyProfile]
  );

  const profile = useMemo(
    () => getWorkoutProfile(newProfile?.external_id ?? ""),
    [getWorkoutProfile, newProfile?.external_id]
  );

  const handleDiscardWorkoutProfile = useCallback(() => {
    if (profile?.external_id) {
      deleteProfile(profile?.external_id);
    }
    setWarningDialogOpen(false);
    setOpen(false);
  }, [deleteProfile, profile?.external_id]);

  const handleContinueEditing = useCallback(() => {
    setWarningDialogOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setWarningDialogOpen(true);
  }, []);

  const handleCloseParent = useCallback(() => {
    enqueueSnackbar({
      message: t`Workout Profile added!`,
      severity: "success",
    });
    closeParent();
  }, [closeParent, enqueueSnackbar]);

  const { workoutProfilesList } = useWorkoutProfiles({
    isNew: true,
    action: handleCopyProfile,
    familyFilter,
  });
  const { templateData, newOrExistingToggle, setNewOrExistingToggle } =
    useAddWorkoutProfile(familyFilter);

  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  const SaveButton = () => {
    return <Button onClick={handleCloseParent}>{t`Done`}</Button>;
  };

  return (
    <React.Fragment>
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        overflow="hidden"
        bgcolor={palette.background.level1}
      >
        <NewOrExistingToggle
          newOrExistingToggle={newOrExistingToggle}
          setNewOrExistingToggle={setNewOrExistingToggle}
        />
        {newOrExistingToggle === "newProfile" && (
          <Box overflow="auto">
            <TemplateChipList templateData={templateData} handleOpen={handleCreateProfile} />
          </Box>
        )}
        {newOrExistingToggle === "existingProfile" && (
          <Box overflow="auto" pt={1}>
            <MenuList
              listItems={workoutProfilesList}
              variant="outlined"
              paperProps={{
                elevation: 0,
                sx: {
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignContent: "flex-start",
                  gap: 1,
                  border: "none",
                  background: palette.background.level1,
                },
              }}
            />
          </Box>
        )}
      </Box>
      <FullScreenDialog
        slideDirection="up"
        open={open}
        handleClose={handleClose}
        headerProps={{
          title: t`Add workout profile`,
          primaryLeftActionItem: {
            text: t`Cancel`,
            action: handleClose,
          },
          primaryRightActionItem: {
            text: t`Done`,
            action: handleCloseParent,
          },
        }}
        backgroundColor={palette.background.default}
      >
        <EditWorkoutProfile
          containedInDialog
          title={t`Add workout profile`}
          profile={profile}
          backAction={handleClose}
          headerAction={<SaveButton />}
          warningDialogOpen={warningDialogOpen}
          onDiscardWorkoutProfile={handleDiscardWorkoutProfile}
          onContinueEditing={handleContinueEditing}
        />
      </FullScreenDialog>
    </React.Fragment>
  );
};

export default AddWorkoutProfile;
